// Volume Availability — hourly arrival / availability analysis for a hub.
//   • Hourly Arrivals — for a hub + date: volume arriving each hour (scheduled
//     loads = green, forecast overflow with no/too-few loads = amber spread
//     across the sort window), the cumulative availability curve, a per-lane
//     LOAD COVERAGE table (over-scheduled / needs-loads / no-schedule), and the
//     scheduled-arrivals list.
//   • 5-Week Profile — the hub's hourly arrival shape across 5 consecutive weeks.
//   • All Hubs — a hub × hour heatmap for one date.
// Pure derivation from the forecast + LH schedule + lane capacity; no new tables.
// See src/lib/forecast/arrivals.ts for the model.

import { Fragment, useEffect, useMemo, useState, type CSSProperties } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { Card, Field, Input, Select } from "../../components/ui";
import { getErrorMessage } from "../../lib/errors";
import { fmtDate, fmtDow, fmtN, today } from "../../lib/format";
import type { Customer, CustomerLane, Hub, HubEvent } from "../../lib/types";
import {
  allHubsHeatmap,
  fiveWeekDetail,
  hourlyAvailability,
  weekDayGrid,
  type AvailabilityOptions,
  type LaneStatus,
} from "../../lib/forecast/arrivals";
import { listCustomers } from "../customers/customersApi";
import { listAccountLanes } from "../customers/lanesApi";
import { listHubs } from "../hubs/hubsApi";
import { listAccountLhSchedule, type LhLaneRow } from "../lhschedule/lhScheduleApi";
import { listHubEvents } from "../hubevents/hubEventsApi";
import { buildOverrideMap, listAccountOverrides, type ForecastOverride } from "./overridesApi";

type Tab = "hourly" | "fiveweek" | "allhubs";
const hourLabel = (h: number) => `${String(h).padStart(2, "0")}:00`;
const HOURS = Array.from({ length: 24 }, (_, h) => h);
const WEEK_COLORS = ["text-slate-600", "text-sky-600", "text-violet-600", "text-emerald-600", "text-amber-600"];
const LINE_COLORS = ["#475569", "#0284c7", "#7c3aed", "#059669", "#d97706"]; // matches WEEK_COLORS

/** Compact number for dense heatmap cells: 1234 → "1.2K", <1 → "". */
const fmtK = (n: number) => {
  const r = Math.round(n);
  if (r < 1) return "";
  return r >= 1000 ? `${(r / 1000).toFixed(1).replace(/\.0$/, "")}K` : String(r);
};

export default function VolumeAvailabilityPage() {
  const { activeAccount } = useAuth();
  const accountId = activeAccount?.accountId ?? null;

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [lanes, setLanes] = useState<Array<{ customerId: string; lane: CustomerLane }>>([]);
  const [lhRows, setLhRows] = useState<LhLaneRow[]>([]);
  const [overrides, setOverrides] = useState<ForecastOverride[]>([]);
  const [events, setEvents] = useState<HubEvent[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [tab, setTab] = useState<Tab>("hourly");
  const [hubId, setHubId] = useState("");
  const [date, setDate] = useState(today());
  const [sortStart, setSortStart] = useState(6);
  const [cutoff, setCutoff] = useState(22);

  useEffect(() => {
    if (!accountId) return;
    Promise.all([
      listCustomers(accountId),
      listHubs(accountId),
      listAccountLanes(accountId),
      listAccountLhSchedule(accountId),
      listAccountOverrides(accountId),
      listHubEvents(accountId),
    ])
      .then(([c, h, ln, lh, ov, ev]) => {
        const activeHubs = h.filter((x) => x.active);
        setCustomers(c);
        setHubs(activeHubs);
        setLanes(ln);
        setLhRows(lh);
        setOverrides(ov);
        setEvents(ev);
        setHubId((prev) => prev || activeHubs[0]?.id || "");
      })
      .catch((e) => setError(getErrorMessage(e, "Failed to load volume availability.")));
  }, [accountId]);

  const overrideMap = useMemo(() => buildOverrideMap(overrides), [overrides]);
  const capByKey = useMemo(() => {
    const m = new Map<string, number | null | undefined>();
    for (const { customerId, lane } of lanes) m.set(`${customerId}|${lane.hubId}`, lane.loadCapacity);
    return m;
  }, [lanes]);

  const opts: AvailabilityOptions = useMemo(
    () => ({ overrides: overrideMap, events, loadCapacity: capByKey, sortStart, cutoff }),
    [overrideMap, events, capByKey, sortStart, cutoff]
  );

  if (!accountId) {
    return (
      <Card>
        <p className="text-sm text-slate-500">No active account. Try signing out and back in.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg font-semibold tracking-tight text-slate-900">Volume Availability</h1>
        <Segmented
          value={tab}
          onChange={setTab}
          options={[
            { value: "hourly", label: "Hourly Arrivals" },
            { value: "fiveweek", label: "5-Week Profile" },
            { value: "allhubs", label: "All Hubs" },
          ]}
        />
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Controls */}
      <Card>
        <div className="flex flex-wrap items-end gap-3">
          {tab !== "allhubs" && (
            <div className="w-48">
              <Field label="Hub">
                <Select value={hubId} onChange={(e) => setHubId(e.target.value)}>
                  {hubs.map((h) => (
                    <option key={h.id} value={h.id}>
                      {h.name}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          )}
          <div className="w-44">
            <Field label={tab === "fiveweek" ? "Anchor date (week 1)" : "Date"}>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value || today())} />
            </Field>
          </div>
          <div className="w-32">
            <Field label="Sort start">
              <Select value={String(sortStart)} onChange={(e) => setSortStart(Number(e.target.value))}>
                {HOURS.map((h) => (
                  <option key={h} value={h}>
                    {hourLabel(h)}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="w-32">
            <Field label="Cutoff">
              <Select value={String(cutoff)} onChange={(e) => setCutoff(Number(e.target.value))}>
                {HOURS.map((h) => (
                  <option key={h} value={h}>
                    {hourLabel(h)}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <p className="max-w-md text-xs text-slate-400">
            Unscheduled / overflow volume is spread across the sort window ({hourLabel(sortStart)}–
            {hourLabel(cutoff)}) as <span className="font-medium text-amber-600">extra</span> fill.
          </p>
        </div>
      </Card>

      {tab === "hourly" && (
        <HourlyTab
          customers={customers}
          lhRows={lhRows}
          hubId={hubId}
          hubName={hubs.find((h) => h.id === hubId)?.name ?? ""}
          date={date}
          opts={opts}
        />
      )}
      {tab === "fiveweek" && (
        <FiveWeekTab customers={customers} lhRows={lhRows} hubId={hubId} date={date} opts={opts} />
      )}
      {tab === "allhubs" && (
        <AllHubsTab customers={customers} lhRows={lhRows} hubs={hubs} date={date} opts={opts} />
      )}
    </div>
  );
}

// ── Hourly Arrivals tab ───────────────────────────────────────────────────────
function HourlyTab({
  customers,
  lhRows,
  hubId,
  hubName,
  date,
  opts,
}: {
  customers: Customer[];
  lhRows: LhLaneRow[];
  hubId: string;
  hubName: string;
  date: string;
  opts: AvailabilityOptions;
}) {
  const avail = useMemo(
    () => (hubId ? hourlyAvailability(customers, lhRows, hubId, date, opts) : null),
    [customers, lhRows, hubId, date, opts]
  );
  const weekGrid = useMemo(
    () => (hubId ? weekDayGrid(customers, lhRows, hubId, date, opts) : null),
    [customers, lhRows, hubId, date, opts]
  );

  const kpi = useMemo(() => {
    if (!avail) return null;
    const totals = avail.byHour.map((b) => b.total);
    const peakHour = totals.reduce((best, v, h) => (v > totals[best] ? h : best), 0);
    return {
      first: avail.arrivals[0]?.time ?? null,
      last: avail.arrivals[avail.arrivals.length - 1]?.time ?? null,
      peakHour,
      peakVol: totals[peakHour],
      byNoonPct: avail.dayTotal > 0 ? (avail.cumulative[11] / avail.dayTotal) * 100 : 0,
    };
  }, [avail]);

  if (!avail || !kpi) return null;

  return (
    <>
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Kpi label="Day total" value={fmtN(avail.dayTotal)} sub={fmtDate(date)} />
        <Kpi label="Scheduled loads" value={fmtN(avail.loads)} sub={`${fmtN(avail.scheduledTotal)} on loads`} />
        <Kpi
          label="Unscheduled extra"
          value={fmtN(avail.extraTotal)}
          sub={avail.needsSchedule > 0 ? `${avail.needsSchedule} lane(s) need loads` : "fully covered"}
          tone={avail.extraTotal > 0 ? "warn" : undefined}
        />
        <Kpi label="Available by 12:00" value={`${Math.round(kpi.byNoonPct)}%`} sub={`${fmtN(avail.cumulative[11])} of ${fmtN(avail.dayTotal)}`} />
        <Kpi label="First / last load" value={kpi.first ? `${kpi.first}–${kpi.last}` : "—"} sub={kpi.first ? "arrival window" : "no scheduled loads"} />
        <Kpi label="Peak hour" value={kpi.peakVol > 0 ? hourLabel(kpi.peakHour) : "—"} sub={kpi.peakVol > 0 ? `${fmtN(kpi.peakVol)} arriving` : "—"} />
      </div>

      <Card>
        <div className="mb-2 flex items-center justify-between">
          <div className="text-sm font-medium text-slate-700">Arrivals by hour + cumulative availability</div>
          <div className="flex items-center gap-4 text-xs text-slate-500">
            <Legend swatch="bg-brand-600" label="scheduled" />
            <Legend swatch="bg-amber-400" label="extra (unscheduled)" />
            <span className="flex items-center gap-1">
              <span className="inline-block h-0.5 w-4 bg-emerald-500" /> available (cumulative)
            </span>
          </div>
        </div>
        <HourChart byHour={avail.byHour} cumulative={avail.cumulative} dayTotal={avail.dayTotal} />
      </Card>

      {/* Load coverage — over-scheduled / needs-loads / no-schedule */}
      <Card className="p-0">
        <div className="border-b border-slate-200 px-5 py-3 text-sm font-medium text-slate-700">
          Load coverage · forecast vs scheduled capacity ({avail.lanes.length} lanes)
        </div>
        {avail.lanes.length === 0 ? (
          <p className="p-5 text-sm text-slate-500">No customer volume at this hub on {fmtDate(date)}.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-5 py-2 font-medium">Customer</th>
                <th className="px-3 py-2 text-right font-medium">Forecast</th>
                <th className="px-3 py-2 text-right font-medium">Loads</th>
                <th className="px-3 py-2 text-right font-medium">Cap / load</th>
                <th className="px-3 py-2 text-right font-medium">Sched. cap</th>
                <th className="px-5 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {avail.lanes.map((l) => (
                <tr key={l.customerId} className="border-b border-slate-100 last:border-0">
                  <td className="px-5 py-2 font-medium text-slate-900">{l.customerName}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700">{fmtN(Math.round(l.forecast))}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">{l.loads || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">{l.capacity ? fmtN(l.capacity) : "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">
                    {l.scheduledCapacity != null ? fmtN(l.scheduledCapacity) : "—"}
                  </td>
                  <td className="px-5 py-2">
                    <StatusBadge status={l.status} extra={l.extra} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Customer × hour matrix (LogiCast-style) */}
      {avail.lanes.length > 0 && (
        <Card className="p-0">
          <div className="border-b border-slate-200 px-5 py-3">
            <div className="text-sm font-medium text-slate-700">Customer × hour</div>
            <p className="mt-0.5 flex items-center gap-3 text-xs text-slate-400">
              When each customer's volume lands.
              <Legend swatch="bg-brand-600" label="scheduled" />
              <Legend swatch="bg-amber-400" label="extra" />
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-200 text-slate-500">
                  <th className="sticky left-0 bg-white px-3 py-2 text-left font-medium">Customer</th>
                  {HOURS.map((h) => (
                    <th key={h} className="px-1 py-2 text-center font-medium tabular-nums">
                      {String(h).padStart(2, "0")}
                    </th>
                  ))}
                  <th className="px-3 py-2 text-right font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {avail.lanes.map((l) => {
                  const max = Math.max(1, ...l.scheduledByHour.map((s, i) => s + l.extraByHour[i]));
                  return (
                    <tr key={l.customerId} className="border-b border-slate-100 last:border-0">
                      <td className="sticky left-0 bg-white px-3 py-1 font-medium text-slate-800">{l.customerName}</td>
                      {HOURS.map((h) => (
                        <td
                          key={h}
                          className="px-1 py-1 text-center tabular-nums"
                          style={matrixCell(l.scheduledByHour[h], l.extraByHour[h], max)}
                          title={`${hourLabel(h)} · ${fmtN(Math.round(l.scheduledByHour[h] + l.extraByHour[h]))}`}
                        >
                          {fmtK(l.scheduledByHour[h] + l.extraByHour[h])}
                        </td>
                      ))}
                      <td className="px-3 py-1 text-right font-medium tabular-nums text-slate-700">{fmtN(Math.round(l.forecast))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="px-5 py-3 text-xs text-slate-400">
            Each row scales to that customer's own busiest hour. Indigo = arriving on a scheduled load;
            amber = unscheduled overflow spread across the sort window.
          </p>
        </Card>
      )}

      {/* Weekly arrival pattern (day × hour) for the selected date's week */}
      {weekGrid && (
        <Card className="p-0">
          <div className="border-b border-slate-200 px-5 py-3">
            <div className="text-sm font-medium text-slate-700">
              Weekly arrival pattern · {hubName} · Day × Hour
            </div>
            <p className="mt-0.5 text-xs text-slate-400">
              The week of the selected date ({fmtDate(weekGrid.weekStart)} – {fmtDate(weekGrid.weekEnd)}).
              Total {fmtN(weekGrid.total)} pkgs. Selected date row outlined in amber.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-200 text-slate-500">
                  <th className="sticky left-0 bg-white px-3 py-2 text-left font-medium">Day</th>
                  {HOURS.map((h) => (
                    <th key={h} className="px-1 py-2 text-center font-medium tabular-nums">
                      {String(h).padStart(2, "0")}
                    </th>
                  ))}
                  <th className="px-3 py-2 text-right font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const max = Math.max(1, ...weekGrid.days.flatMap((d) => d.byHour));
                  return weekGrid.days.map((d) => {
                    const sel = d.date === date;
                    return (
                      <tr
                        key={d.date}
                        className={`border-b border-slate-100 last:border-0 ${sel ? "outline outline-2 -outline-offset-2 outline-amber-400" : ""}`}
                      >
                        <td className={`sticky left-0 px-3 py-1.5 font-medium ${sel ? "bg-amber-50 text-amber-700" : "bg-white text-slate-700"}`}>
                          {fmtDow(d.date)}
                        </td>
                        {d.byHour.map((v, h) => (
                          <td key={h} className="px-1 py-1.5 text-center tabular-nums" style={cellStyle(v, max)} title={`${hourLabel(h)} · ${fmtN(Math.round(v))}`}>
                            {fmtK(v)}
                          </td>
                        ))}
                        <td className="px-3 py-1.5 text-right font-medium tabular-nums text-slate-700">{fmtN(Math.round(d.total))}</td>
                      </tr>
                    );
                  });
                })()}
                <tr className="border-t-2 border-slate-200 bg-slate-50">
                  <td className="sticky left-0 bg-slate-50 px-3 py-1.5 font-semibold text-slate-600">Hour total</td>
                  {weekGrid.byHour.map((v, h) => (
                    <td key={h} className="px-1 py-1.5 text-center font-semibold tabular-nums text-slate-600">{fmtK(v)}</td>
                  ))}
                  <td className="px-3 py-1.5 text-right font-semibold tabular-nums text-slate-700">{fmtN(Math.round(weekGrid.total))}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Scheduled arrivals */}
      <Card className="p-0">
        <div className="border-b border-slate-200 px-5 py-3 text-sm font-medium text-slate-700">
          Scheduled arrivals ({avail.arrivals.length})
        </div>
        {avail.arrivals.length === 0 ? (
          <p className="p-5 text-sm text-slate-500">No linehaul arrival times scheduled for this hub on {fmtDate(date)}.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-5 py-2 font-medium">Time</th>
                <th className="px-3 py-2 font-medium">Customer</th>
                <th className="px-3 py-2 text-right font-medium">Load</th>
                <th className="px-5 py-2 text-right font-medium">Volume</th>
              </tr>
            </thead>
            <tbody>
              {avail.arrivals.map((a, i) => (
                <tr key={`${a.customerId}-${a.time}-${i}`} className="border-b border-slate-100 last:border-0">
                  <td className="px-5 py-2 font-medium tabular-nums text-slate-900">{a.time}</td>
                  <td className="px-3 py-2 text-slate-700">{a.customerName}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">
                    {a.loadIndex}/{a.loadCount}
                  </td>
                  <td className="px-5 py-2 text-right tabular-nums text-slate-700">{fmtN(Math.round(a.volume))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

// ── 5-Week Profile tab ────────────────────────────────────────────────────────
function FiveWeekTab({
  customers,
  lhRows,
  hubId,
  date,
  opts,
}: {
  customers: Customer[];
  lhRows: LhLaneRow[];
  hubId: string;
  date: string;
  opts: AvailabilityOptions;
}) {
  const detail = useMemo(
    () => (hubId ? fiveWeekDetail(customers, lhRows, hubId, date, opts) : null),
    [customers, lhRows, hubId, date, opts]
  );
  const [openWeeks, setOpenWeeks] = useState<Set<string>>(new Set());
  const [openCusts, setOpenCusts] = useState<Set<string>>(new Set());
  if (!detail) return null;

  const TOP_CUST = 25;
  const shownCustomers = detail.customers.slice(0, TOP_CUST);
  const toggle = (setter: typeof setOpenWeeks) => (key: string) =>
    setter((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  const toggleWeek = toggle(setOpenWeeks);
  const toggleCust = toggle(setOpenCusts);

  // Per-customer hour totals summed across the 5 weeks (collapsed-row values).
  const custTotals = shownCustomers.map((c) => {
    const byHour = new Array<number>(24).fill(0);
    for (const wk of c.weeks) for (let h = 0; h < 24; h++) byHour[h] += wk.byHour[h];
    return byHour;
  });
  const maxCustTotalHour = Math.max(1, ...custTotals.flat());

  return (
    <>
      {/* Top graph: 5-week hourly overlay */}
      <Card>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-medium text-slate-700">Hourly arrival pattern · 5-week overlay</div>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            {detail.weeks.map((w, i) => (
              <span key={w.weekStart} className="flex items-center gap-1 text-slate-500">
                <span className="inline-block h-0.5 w-4" style={{ backgroundColor: LINE_COLORS[i] }} />
                {fmtDate(w.weekEnd)}
              </span>
            ))}
          </div>
        </div>
        <WeekLines weeks={detail.weeks} max={detail.maxWeekHour} />
      </Card>

      {/* Week × hour — each week expands to its days */}
      <Card className="p-0">
        <div className="border-b border-slate-200 px-5 py-3">
          <div className="text-sm font-medium text-slate-700">
            Hourly arrival profile · 5 weeks from {fmtDate(detail.weeks[0].weekStart)}
          </div>
          <p className="mt-0.5 text-xs text-slate-400">
            Each cell is the total volume arriving in that hour across the week. Click a week to expand it
            into its days.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-200 text-slate-500">
                <th className="sticky left-0 bg-white px-3 py-2 text-left font-medium">Week ending</th>
                {HOURS.map((h) => (
                  <th key={h} className="px-1 py-2 text-center font-medium tabular-nums">
                    {String(h).padStart(2, "0")}
                  </th>
                ))}
                <th className="px-3 py-2 text-right font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {detail.weeks.map((w) => {
                const open = openWeeks.has(w.weekStart);
                return (
                  <Fragment key={w.weekStart}>
                    <tr
                      className="cursor-pointer border-b border-slate-100 hover:bg-slate-50"
                      onClick={() => toggleWeek(w.weekStart)}
                    >
                      <td className="sticky left-0 bg-white px-3 py-1.5 font-medium text-slate-700">
                        <span className="mr-1 text-brand-600">{open ? "▾" : "▸"}</span>
                        {fmtDate(w.weekEnd)}
                      </td>
                      {w.byHour.map((v, h) => (
                        <td key={h} className="px-1 py-1.5 text-center tabular-nums" style={cellStyle(v, detail.maxWeekHour)} title={`${hourLabel(h)} · ${fmtN(Math.round(v))}`}>
                          {fmtK(v)}
                        </td>
                      ))}
                      <td className="px-3 py-1.5 text-right font-medium tabular-nums text-slate-700">{fmtN(Math.round(w.total))}</td>
                    </tr>
                    {open &&
                      w.days.map((d) => (
                        <tr key={d.date} className="border-b border-slate-100 bg-slate-50/40 last:border-0">
                          <td className="sticky left-0 bg-slate-50/40 py-1 pl-8 pr-3 text-slate-500">{fmtDow(d.date)}</td>
                          {d.byHour.map((v, h) => (
                            <td key={h} className="px-1 py-1 text-center tabular-nums" style={cellStyle(v, detail.maxDayHour)} title={`${hourLabel(h)} · ${fmtN(Math.round(v))}`}>
                              {fmtK(v)}
                            </td>
                          ))}
                          <td className="px-3 py-1 text-right tabular-nums text-slate-500">{fmtN(Math.round(d.total))}</td>
                        </tr>
                      ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Customer arrival times — each customer expands to its weeks */}
      <Card className="p-0">
        <div className="border-b border-slate-200 px-5 py-3">
          <div className="text-sm font-medium text-slate-700">Customer arrival times — weekly comparison</div>
          <p className="mt-0.5 text-xs text-slate-400">
            Collapsed row = 5-week total per hour. Click a customer to expand into its weeks.
            {detail.customers.length > TOP_CUST && ` Showing top ${TOP_CUST} of ${detail.customers.length} customers.`}
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-200 text-slate-500">
                <th className="sticky left-0 bg-white px-3 py-2 text-left font-medium">Customer / week ending</th>
                {HOURS.map((h) => (
                  <th key={h} className="px-1 py-2 text-center font-medium tabular-nums">
                    {String(h).padStart(2, "0")}
                  </th>
                ))}
                <th className="px-3 py-2 text-right font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {shownCustomers.map((c, ci) => {
                const open = openCusts.has(c.customerId);
                return (
                  <Fragment key={c.customerId}>
                    <tr
                      className="cursor-pointer border-b border-t border-slate-200 hover:bg-slate-50"
                      onClick={() => toggleCust(c.customerId)}
                    >
                      <td className="sticky left-0 bg-white px-3 py-1.5 font-medium text-slate-800">
                        <span className="mr-1 text-brand-600">{open ? "▾" : "▸"}</span>
                        {c.customerName}
                      </td>
                      {custTotals[ci].map((v, h) => (
                        <td key={h} className="px-1 py-1.5 text-center tabular-nums" style={cellStyle(v, maxCustTotalHour)} title={`${hourLabel(h)} · ${fmtN(Math.round(v))}`}>
                          {fmtK(v)}
                        </td>
                      ))}
                      <td className="px-3 py-1.5 text-right font-medium tabular-nums text-slate-700">{fmtN(Math.round(c.grandTotal))}</td>
                    </tr>
                    {open &&
                      c.weeks.map((wk, i) => (
                        <tr key={i} className="border-b border-slate-100 bg-slate-50/40 last:border-0">
                          <td className={`sticky left-0 bg-slate-50/40 whitespace-nowrap py-1 pl-8 pr-3 font-medium ${WEEK_COLORS[i]}`}>
                            {fmtDate(detail.weeks[i].weekEnd)}
                          </td>
                          {wk.byHour.map((v, h) => (
                            <td key={h} className="px-1 py-1 text-center tabular-nums" style={cellStyle(v, detail.maxCustHour)} title={`${hourLabel(h)} · ${fmtN(Math.round(v))}`}>
                              {fmtK(v)}
                            </td>
                          ))}
                          <td className="px-3 py-1 text-right tabular-nums text-slate-500">{fmtN(Math.round(wk.total))}</td>
                        </tr>
                      ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {shownCustomers.length === 0 && <p className="p-5 text-sm text-slate-500">No customer volume at this hub in these 5 weeks.</p>}
      </Card>
    </>
  );
}

// ── All Hubs tab ──────────────────────────────────────────────────────────────
function AllHubsTab({
  customers,
  lhRows,
  hubs,
  date,
  opts,
}: {
  customers: Customer[];
  lhRows: LhLaneRow[];
  hubs: Hub[];
  date: string;
  opts: AvailabilityOptions;
}) {
  const hm = useMemo(
    () => allHubsHeatmap(customers, lhRows, hubs, date, opts),
    [customers, lhRows, hubs, date, opts]
  );

  return (
    <Card className="p-0">
      <div className="border-b border-slate-200 px-5 py-3 text-sm font-medium text-slate-700">
        Hub × hour heatmap · {fmtDate(date)}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-200 text-slate-500">
              <th className="sticky left-0 bg-white px-3 py-2 text-left font-medium">Hub</th>
              {HOURS.map((h) => (
                <th key={h} className="px-1 py-2 text-center font-medium tabular-nums">
                  {String(h).padStart(2, "0")}
                </th>
              ))}
              <th className="px-3 py-2 text-right font-medium">Total</th>
            </tr>
          </thead>
          <tbody>
            {hm.rows.map((r) => (
              <tr key={r.hubId} className="border-b border-slate-100 last:border-0">
                <td className="sticky left-0 bg-white px-3 py-1.5 font-medium text-slate-700">{r.hubName}</td>
                {r.byHour.map((v, h) => (
                  <td
                    key={h}
                    className="px-1 py-1.5 text-center tabular-nums"
                    style={cellStyle(v, hm.maxCell)}
                    title={`${r.hubName} · ${hourLabel(h)} · ${fmtN(Math.round(v))}`}
                  >
                    {v >= 1 ? fmtN(Math.round(v)) : ""}
                  </td>
                ))}
                <td className="px-3 py-1.5 text-right font-medium tabular-nums text-slate-700">{fmtN(Math.round(r.total))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="px-5 py-3 text-xs text-slate-400">
        Each cell is the total volume arriving at that hub in that hour (scheduled + extra fill). Darker
        = busier. Use it to spot hubs whose inbound peaks land at the same hour.
      </p>
    </Card>
  );
}

// ── Shared bits ───────────────────────────────────────────────────────────────
function Segmented<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: string }>;
}) {
  return (
    <div className="inline-flex overflow-hidden rounded-lg border border-slate-300">
      {options.map((o, i) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`px-3 py-1.5 text-xs font-medium ${i > 0 ? "border-l border-slate-300" : ""} ${
            value === o.value ? "bg-brand-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: "warn" }) {
  return (
    <Card>
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`mt-1 text-xl font-semibold ${tone === "warn" ? "text-amber-600" : "text-slate-900"}`}>{value}</p>
      {sub && <p className="text-[11px] text-slate-400">{sub}</p>}
    </Card>
  );
}

function Legend({ swatch, label }: { swatch: string; label: string }) {
  return (
    <span className="flex items-center gap-1">
      <span className={`inline-block h-2 w-3 rounded-sm ${swatch}`} /> {label}
    </span>
  );
}

function StatusBadge({ status, extra }: { status: LaneStatus; extra: number }) {
  const map: Record<LaneStatus, { cls: string; text: string }> = {
    ok: { cls: "bg-emerald-50 text-emerald-700", text: "scheduled" },
    over: { cls: "bg-amber-50 text-amber-700", text: "over-scheduled (trucks under-filled)" },
    under: { cls: "bg-red-50 text-red-700", text: `needs loads · +${fmtN(Math.round(extra))} extra` },
    unscheduled: { cls: "bg-red-50 text-red-700", text: "no schedule" },
  };
  const m = map[status];
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${m.cls}`}>{m.text}</span>;
}

/** Heatmap cell background: indigo intensity scaling with value / max. */
function cellStyle(v: number, max: number): CSSProperties {
  if (v < 1) return {};
  const alpha = Math.min(0.85, 0.08 + (v / max) * 0.77);
  return { backgroundColor: `rgba(15, 118, 110, ${alpha})`, color: alpha > 0.5 ? "white" : "#334155" };
}

/** Customer×hour cell: indigo when scheduled dominates, amber when extra dominates. */
function matrixCell(scheduled: number, extra: number, max: number): CSSProperties {
  const v = scheduled + extra;
  if (v < 1) return {};
  const rgb = extra > scheduled ? "245, 158, 11" : "15, 118, 110";
  const alpha = Math.min(0.85, 0.08 + (v / max) * 0.77);
  return { backgroundColor: `rgba(${rgb}, ${alpha})`, color: alpha > 0.5 ? "white" : "#334155" };
}

// 5-week hourly overlay: one polyline per week (hours 0–23 × volume).
function WeekLines({ weeks, max }: { weeks: Array<{ weekEnd: string; byHour: number[] }>; max: number }) {
  const W = 720;
  const H = 150;
  const padL = 8;
  const padR = 8;
  const padT = 10;
  const padB = 22;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const x = (h: number) => padL + (h / 23) * plotW;
  const y = (v: number) => padT + plotH - (v / max) * plotH;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-3xl" role="img" aria-label="5-week hourly overlay">
      <line x1={padL} y1={padT + plotH} x2={W - padR} y2={padT + plotH} stroke="#e2e8f0" />
      {weeks.map((w, i) => (
        <polyline
          key={w.weekEnd}
          points={w.byHour.map((v, h) => `${x(h)},${y(v)}`).join(" ")}
          fill="none"
          stroke={LINE_COLORS[i]}
          strokeWidth={i === 0 ? 2.5 : 1.5}
          opacity={0.9}
        />
      ))}
      {HOURS.map((h) =>
        h % 3 === 0 ? (
          <text key={h} x={x(h)} y={H - 6} textAnchor="middle" className="fill-slate-400 text-[10px]">
            {String(h).padStart(2, "0")}
          </text>
        ) : null
      )}
    </svg>
  );
}

// Hand-rolled SVG: stacked hourly bars (scheduled + extra) + cumulative line.
function HourChart({
  byHour,
  cumulative,
  dayTotal,
}: {
  byHour: Array<{ scheduled: number; extra: number; total: number }>;
  cumulative: number[];
  dayTotal: number;
}) {
  const W = 720;
  const H = 175;
  const padL = 8;
  const padR = 8;
  const padT = 12;
  const padB = 24;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const barMax = Math.max(1, ...byHour.map((b) => b.total));
  const slot = plotW / 24;
  const barW = slot * 0.62;

  const x = (h: number) => padL + h * slot + slot / 2;
  const scale = (v: number) => (v / barMax) * plotH;
  const lineY = (v: number) => padT + plotH - (dayTotal > 0 ? (v / dayTotal) * plotH : 0);
  const linePts = cumulative.map((v, h) => `${x(h)},${lineY(v)}`).join(" ");

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-3xl" role="img" aria-label="Arrivals by hour">
      <line x1={padL} y1={padT + plotH} x2={W - padR} y2={padT + plotH} stroke="#e2e8f0" />
      {byHour.map((b, h) => {
        const schedH = scale(b.scheduled);
        const extraH = scale(b.extra);
        const baseY = padT + plotH;
        const tot = b.scheduled + b.extra;
        const lbl = tot >= 1000 ? `${(tot / 1000).toFixed(tot >= 10000 ? 0 : 1)}K` : Math.round(tot);
        return (
          <g key={h}>
            {b.scheduled > 0 && (
              <rect x={x(h) - barW / 2} y={baseY - schedH} width={barW} height={schedH} rx={1} fill="#0f766e" />
            )}
            {b.extra > 0 && (
              <rect x={x(h) - barW / 2} y={baseY - schedH - extraH} width={barW} height={extraH} rx={1} fill="#fbbf24" />
            )}
            {tot > 0 && (
              <text x={x(h)} y={baseY - schedH - extraH - 3} textAnchor="middle" className="fill-slate-500 text-[7px] tabular-nums">
                {lbl}
              </text>
            )}
          </g>
        );
      })}
      <polyline points={linePts} fill="none" stroke="#10b981" strokeWidth={2} />
      {HOURS.map((h) =>
        h % 3 === 0 ? (
          <text key={h} x={x(h)} y={H - 6} textAnchor="middle" className="fill-slate-400 text-[10px]">
            {String(h).padStart(2, "0")}
          </text>
        ) : null
      )}
    </svg>
  );
}
